import { LanguageSelectComponent } from '@common/ui/language-select/language-select.component';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  type OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, type FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { type RoleDTO, UserProfileWriteDTO, type WarehouseSimpleDTO } from '@api/models';
import { RoleService } from '@api/services/role.service';
import { UserService } from '@api/services/user.service';
import { WarehouseService } from '@api/services/warehouse.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { AlertService } from '@infra/error-handling/alert.service';
import { RoleSelectComponent } from '@shared/users/role-select/role-select.component';
import { AuthService } from '@infra/auth/auth.service';
import { SecurityService } from '@infra/auth/security.service';
import { MultiSelectModule } from 'primeng/multiselect';
import { forkJoin, iif, map, mergeMap } from 'rxjs';

@Component({
  selector: 'app-user-profile-edit',
  templateUrl: './user-profile-edit.component.html',
  imports: [ReactiveFormsModule, TranslatePipe, LanguageSelectComponent, MultiSelectModule, RoleSelectComponent],
})
export class UserProfileEditComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly alertService = inject(AlertService);
  private readonly warehouseService = inject(WarehouseService);
  private readonly roleService = inject(RoleService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly securityService = inject(SecurityService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);

  @ViewChild('uploadImageButton', { static: false }) uploadImageButton: ElementRef;
  @Input() modal: NgbActiveModal;
  @Input() userId: string = null;
  @Output() editComplete = new EventEmitter();

  profileForm: FormGroup = this.formBuilder.group({
    avatarUrl: [''],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    email: new FormControl({ value: '', disabled: true }),
    phone: [''],
    externalReference: [''],
    languageCode: [this.translateService.getFallbackLang()],
    isAgentAdmin: [false],
    isTodoOwner: [false],
    enableNotificationsViaMail: [false],
    roleTypes: [[]],
    warehouses: [[]],
  });
  roleTypes: RoleDTO[] = [];
  warehouses: WarehouseSimpleDTO[] = [];
  isSelfProfile = false;

  ngOnInit(): void {
    this.userService
      .get(this.userId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap(user => this.securityService.currentUser$.pipe(map(currentUser => ({ currentUser, user })))),
        mergeMap(({ currentUser, user }) =>
          this.roleService.listReedgeRolesForCompany().pipe(map(roles => ({ currentUser, user, roles })))
        )
      )
      .subscribe(({ currentUser, user, roles }) => {
        this.isSelfProfile = user.id === currentUser.id;

        this.profileForm.patchValue({
          avatarUrl: user.avatarUrl,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone,
          externalReference: user.externalReference,
          languageCode: user.languageCode,
          isAgentAdmin: user.isAgentAdmin,
          isTodoOwner: user.isTodoOwner,
          enableNotificationsViaMail: user.enableNotificationsViaMail,
        });

        this.roleTypes = roles;

        if (!this.isSelfProfile) {
          const userRoles: RoleDTO[] = this.roleTypes.filter(x => user.userRoles.some(y => y.roleId === x.id));
          this.profileForm.controls.roleTypes.setValue(userRoles);
          if (roles.length > 0) {
            this.profileForm.controls.roleTypes.addValidators(Validators.required);
          }
        }
      });

    this.warehouseService
      .listSimple()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap(warehouses =>
          this.userService.listWarehouses(this.userId).pipe(map(userWarehouses => ({ warehouses, userWarehouses })))
        )
      )
      .subscribe(({ warehouses, userWarehouses }) => {
        this.warehouses = warehouses;
        this.profileForm.controls.warehouses.setValue(
          userWarehouses.map(x => ({
            id: x.id,
            name: x.name,
          }))
        );
      });
  }

  saveProfile(): void {
    const roleTypes = this.profileForm.controls.roleTypes.value;
    const roleTypeIds = roleTypes.map(x => x.id);
    const warehouses = this.profileForm.controls.warehouses.value;
    const warehouseIds = warehouses.map(x => x.id);

    const hasRoles = roleTypeIds.length > 0;

    if (this.canEditRoles() && !hasRoles) {
      this.alertService.warn(`$message_please_select_role$`);
    } else {
      const request: UserProfileWriteDTO = {
        email: this.profileForm.controls.email.value,
        avatarUrl: this.profileForm.controls.avatarUrl.value,
        firstName: this.profileForm.controls.firstName.value,
        lastName: this.profileForm.controls.lastName.value,
        phone: this.profileForm.controls.phone.value,
        externalReference: this.profileForm.controls.externalReference.value,
        languageCode: this.profileForm.controls.languageCode.value,
        isAgentAdmin: this.profileForm.controls.isAgentAdmin.value,
        isTodoOwner: this.profileForm.controls.isTodoOwner.value,
        enableNotificationsViaMail: this.profileForm.controls.enableNotificationsViaMail.value,
      };

      const isLanguageChanged = this.profileForm.controls.languageCode.dirty;

      this.userService
        .editProfile(this.userId, request)
        .pipe(
          mergeMap(() =>
            iif(
              () => this.isSelfProfile,
              this.securityService.getNewToken(this.authService.companyUrlName),
              forkJoin([
                this.userService.updateRoles(this.userId, roleTypeIds),
                this.userService.updateWarehouses(this.userId, warehouseIds),
              ])
            )
          ),
          mergeMap(() => this.userService.get(this.userId))
        )
        .subscribe(user => {
          if (user) {
            this.roleTypes = roleTypes;

            this.editComplete.emit();
            this.alertService.success();
            this.modal.dismiss();

            if (this.isSelfProfile) {
              this.securityService.setLoggedInUser(user);

              if (isLanguageChanged) {
                window.location.reload();
              }
            }
          }
        });
    }
  }

  cancelEdit(): void {
    this.modal.dismiss();
  }

  uploadImage(files: FileList): void {
    const fileSize = files[0].size / (1024 * 1024);

    if (fileSize > 50) {
      this.alertService.error(`$message_file_exceeds_size_limit$`);
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(files[0]);

    this.userService.uploadAvatar(files[0]).subscribe(avatarUrl => {
      if (avatarUrl) {
        this.profileForm.controls.avatarUrl.setValue(avatarUrl);
        this.profileForm.controls.avatarUrl.markAsDirty();
        this.profileForm.controls.avatarUrl.markAsTouched();
        this.uploadImageButton.nativeElement.value = null;
      }
    });
  }

  showValidationErrors(controlName: string): boolean {
    return (
      this.profileForm.controls[controlName].invalid &&
      (this.profileForm.controls[controlName].dirty || this.profileForm.controls[controlName].touched)
    );
  }

  canEditRoles(): boolean {
    return !this.isSelfProfile && this.roleTypes?.length > 0;
  }
}
